import { Services } from '@sigma-frontend-libs/commons'
import messages from '@sigma-frontend-libs/messages'
import { CountryRegionsOpenApiSchema } from '@sigmasms/shared-types'

import { getOpenApiSchemaProperty, getOpenApiSchemaPropertyConstValues, getTranslate, isOpenApiSchemaPropertyRequired } from '@/utils'

/**
 * Поля записи региона в порядке вывода в форме и в элементе списка.
 *
 * @type {string[]}
 */
export const countryRegionsFields = [ 'regionCode', 'regionName', 'countryCode', 'countryName', 'timeZone' ]

/**
 * Поля фильтра списка регионов.
 *
 * @type {string[]}
 */
export const countryRegionsFilterFields = [ 'regionCode', 'countryCode' ]

/**
 * Коды стран из словаря `countries.codes` библиотеки сообщений.
 *
 * @type {string[]}
 */
export const countryRegionsCountryCodes = Object.keys(messages.ru.countries.codes)

/**
 * Маска ввода, переводящая введённые символы в верхний регистр без ограничения набора символов.
 *
 * @type {{ mask: RegExp, maskOptions: { prepareChar: Function } }}
 */
export const countryRegionsUpperCaseInputMask = {
  mask: /^.*$/,
  maskOptions: {
    prepareChar: value => {
      return value.toUpperCase()
    }
  }
}

/**
 * Возвращает часовой пояс в формате `UTC+HH:MM` или `UTC-HH:MM` по смещению от UTC.
 *
 * @param {number} offsetMinutes Смещение от UTC в минутах.
 * @returns {string} Часовой пояс.
 */
export const formatCountryRegionsTimeZone = offsetMinutes => {
  let sign = '+'
  if (offsetMinutes < 0) {
    sign = '-'
  }
  const absoluteMinutes = Math.abs(offsetMinutes)
  const hours = String(Math.floor(absoluteMinutes / 60)).padStart(2, '0')
  const minutes = String(absoluteMinutes % 60).padStart(2, '0')

  return `UTC${sign}${hours}:${minutes}`
}

/**
 * Часовые пояса от `UTC-12:00` до `UTC+14:00` с шагом 30 минут.
 *
 * @type {string[]}
 */
export const countryRegionsTimeZones = Array.from({ length: (14 + 12) * 2 + 1 }, (item, index) => {
  return formatCountryRegionsTimeZone(-12 * 60 + index * 30)
})

/**
 * Возвращает имя схемы тела запроса создания или изменения записи в `CountryRegionsOpenApiSchema`.
 *
 * @param {boolean} isNew Признак создания новой записи.
 * @returns {string} Имя схемы в `components.schemas`.
 */
const getCountryRegionsBodySchemaName = isNew => {
  if (isNew) {
    return 'CountryRegionsCreateReqBodyDtoValidator'
  }

  return 'CountryRegionsUpdateReqBodyDtoValidator'
}

/**
 * Возвращает описание поля тела запроса создания или изменения записи из `CountryRegionsOpenApiSchema`.
 *
 * @param {string} fieldName Имя поля записи.
 * @param {boolean} isNew Признак создания новой записи.
 * @returns {object|undefined} Описание поля с ограничениями `minLength`, `maxLength` и `pattern`.
 */
export const getCountryRegionsBodyProperty = (fieldName, isNew) => {
  return getOpenApiSchemaProperty(CountryRegionsOpenApiSchema, getCountryRegionsBodySchemaName(isNew), fieldName)
}

/**
 * Проверяет по `required` схемы тела запроса создания или изменения записи, что поле обязательно.
 *
 * @param {string} fieldName Имя поля записи.
 * @param {boolean} isNew Признак создания новой записи.
 * @returns {boolean} Признак обязательного поля.
 */
export const isCountryRegionsBodyFieldRequired = (fieldName, isNew) => {
  return isOpenApiSchemaPropertyRequired(CountryRegionsOpenApiSchema, getCountryRegionsBodySchemaName(isNew), fieldName)
}

/**
 * Возвращает описание поля фильтра списка записей из `CountryRegionsOpenApiSchema`.
 *
 * @param {string} fieldName Имя поля фильтра.
 * @returns {object|undefined} Описание поля с ограничениями `minLength`, `maxLength` и `pattern`.
 */
export const getCountryRegionsFilterProperty = fieldName => {
  return getOpenApiSchemaProperty(CountryRegionsOpenApiSchema, 'CountryRegionsGetManyReqQueryDtoValidator', fieldName)
}

/**
 * Поля сортировки списка регионов из вариантов поля `orderField` схемы запроса списка.
 *
 * @type {string[]}
 */
export const countryRegionsOrderFields = getOpenApiSchemaPropertyConstValues(getCountryRegionsFilterProperty('orderField'))

/**
 * Возвращает текст правила длины поля с подставленными из описания поля `minLength` и `maxLength`.
 * При совпадении `minLength` и `maxLength` используется правило точной длины.
 *
 * @param {object} property Описание поля из схемы.
 * @returns {string} Текст правила длины.
 */
export const getCountryRegionsLengthRule = (property = {}) => {
  let rule = 'range'
  if (property.minLength === property.maxLength) {
    rule = 'exact'
  }

  return getTranslate(`${Services['country-regions']}.rules.length.${rule}`, {
    minLength: property.minLength,
    maxLength: property.maxLength
  })
}

/**
 * Возвращает текст правила формата поля.
 *
 * @param {string} fieldName Имя поля записи.
 * @returns {string} Текст правила формата.
 */
export const getCountryRegionsPatternRule = fieldName => {
  return getTranslate(`${Services['country-regions']}.rules.pattern.${fieldName}`)
}
